<template>
  <RevCard class="p-32">
    <h2 class="heading-2">
      {{ i18n(translations.title) }}
    </h2>

    <div class="body-1 mt-8">
      {{ i18n(translations.subtitle) }}
    </div>
    <Filters
      v-model:filtersValues="filtersValues"
      :resetFilters
      :submitFilters
    />
    <RevCard class="mt-32">
      <RevLoadingScreen
        v-if="isFinancialReportPending"
        class="mt-24 p-24"
        :text="i18n(translations.loading)"
      />
      <FinancialReportTables
        v-if="shouldShowResults"
        :report="financialReport!"
      />
    </RevCard>
    <NoResults v-if="shouldShowNoResults" class="p-16" />
  </RevCard>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'

import { type Currency, merchantsAPI } from '@backmarket/http-api'
import type {
  FinancialYearOnYearReport,
  FinancialYearReport,
  KpiReport,
  formattedFinancialReport,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { useFilters } from '~/composables/useFilters'

import translations from './FinancialReport.translations'
import { getDefaultFilterValues } from './components/Filters/Filters.utils'
import Filters from './components/Filters/Filters.vue'
import NoResults from './components/NoResults/NoResults.vue'
import FinancialReportTables from './components/Tables/FinancialReportTables.vue'

const i18n = useI18n()

const { openErrorToast } = useTheToast()

const sellerCurrency = useMarketplace().market.defaultCurrency
const initialFiltersValues = getDefaultFilterValues(sellerCurrency)
const { sanitizedFiltersValues, filtersValues, submitFilters, resetFilters } =
  useFilters(initialFiltersValues)

enum KpiReportType {
  LAST_YEAR = 'lastYear',
  THIS_YEAR = 'thisYear',
  YEAR_ON_YEAR = 'yearOnYear',
}

/**
 * This method formats the amounts of a financial report to display them in the table, with the correct currency and unit
 * if the amount is a percentage, it will be displayed with the percentage sign
 */
function formatAmounts(
  amounts: FinancialYearReport | FinancialYearOnYearReport,
  currency: Currency,
): FinancialYearReport | FinancialYearOnYearReport {
  return Object.entries(amounts).reduce(
    (acc, [key, value]) => {
      let formattedValue = '\u00A0'

      if (value)
        formattedValue =
          key === 'percentage'
            ? i18n(translations.percentage, { percentage: value })
            : i18n.price(value, { currency })

      return {
        ...acc,
        [key]: formattedValue,
      }
    },
    {} as FinancialYearReport | FinancialYearOnYearReport,
  )
}

/**
 * This method extracts the specified year report type (last year, this year or year to year) from each KPI report of a financial report
 */
function extractReports(
  report: KpiReport[],
  key: KpiReportType,
): (FinancialYearReport | FinancialYearOnYearReport)[] {
  if (!report) return []

  return report.map(
    (
      metricReport: KpiReport,
    ): FinancialYearReport | FinancialYearOnYearReport => {
      return {
        ...formatAmounts(metricReport?.[key], metricReport?.currency),
        metric: metricReport.metric.code,
      }
    },
  )
}

/**
 * Format a raw financial report fetched by API
 */
function transformRawFinancialReport(
  report: merchantsAPI.GetFinancialReportResponse,
): formattedFinancialReport | null {
  const kpiReports = report.results

  if (kpiReports.length === 0) return null

  // Sort report descending based on the `thisYear` field value
  report.results.sort((a, b) => {
    return (
      parseFloat(b.thisYear.amountTotal) - parseFloat(a.thisYear.amountTotal)
    )
  })

  // Move the total KPI report to the end of the array as it is always the last row of the table
  const totalKpiReportIndex = kpiReports.findIndex(
    (metricReport) => metricReport.metric.code === 'TOTAL',
  )

  if (totalKpiReportIndex !== -1) {
    const totalKpiReport = kpiReports[totalKpiReportIndex]

    kpiReports.splice(totalKpiReportIndex, 1)
    kpiReports.push(totalKpiReport)
  }

  const lastYearReport = extractReports(
    kpiReports,
    KpiReportType.LAST_YEAR,
  ) as FinancialYearReport[]

  const thisYearReport = extractReports(
    kpiReports,
    KpiReportType.THIS_YEAR,
  ) as FinancialYearReport[]

  const yearOnYearReport = extractReports(
    kpiReports,
    KpiReportType.YEAR_ON_YEAR,
  ) as FinancialYearOnYearReport[]

  return {
    lastYearReport,
    thisYearReport,
    yearOnYearReport,
    lastYearTitle: String(
      new Date(report.results[0].endDate).getFullYear() - 1,
    ),
    thisYearTitle: String(new Date(report.results[0].endDate).getFullYear()),
    metrics: kpiReports.map((metricReport) => metricReport.metric),
  }
}

const {
  data: financialReport,
  error: financialReportFetchError,
  pending: isFinancialReportPending,
} = await useHttpFetch(merchantsAPI.getFinancialReport, {
  queryParams: sanitizedFiltersValues,
  watch: [sanitizedFiltersValues],
  transform: transformRawFinancialReport,
})

const shouldShowResults = computed(
  () => !!financialReport.value && !isFinancialReportPending.value,
)

const shouldShowNoResults = computed(
  () => !financialReport.value && !isFinancialReportPending.value,
)

watch(
  financialReportFetchError,
  () => {
    if (financialReportFetchError.value) {
      openErrorToast()
    }
  },
  { immediate: true },
)
</script>
